/**
 * Day-2 Owner workflow: sources → evidence → reviewable Business Profile,
 * and every later stage of the run below it. The page loads the run, owns
 * the state the sections edit (so a reload never resets a form), and
 * composes the sections under `analysis-run/` in stage order.
 */

import { useCallback, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { fetchAnalysisRun } from '../api.js';
import { FailurePanel, LoadingPanel, useAsync } from './common.js';
import { AnalysisPipeline, AnalysisStrategyBrief } from './AnalysisStrategyBrief.js';
import { AudienceComparisonSection } from './analysis-run/AudienceComparisonSection.js';
import { BusinessProfileEditor } from './analysis-run/BusinessProfileEditor.js';
import { BusinessSourcesSection } from './analysis-run/BusinessSourcesSection.js';
import { CompetitorSection } from './analysis-run/CompetitorSection.js';
import { ContentCollectionSection } from './analysis-run/ContentCollectionSection.js';
import { ContentUnderstandingSection } from './analysis-run/ContentUnderstandingSection.js';
import { CreativeDirectionsSection } from './analysis-run/CreativeDirectionsSection.js';
import { FindingsReviewSection } from './analysis-run/FindingsReviewSection.js';
import { OpportunitiesSection } from './analysis-run/OpportunitiesSection.js';
import { ReadinessAndEvidenceSection } from './analysis-run/ReadinessAndEvidenceSection.js';
import { RunHeader } from './analysis-run/RunHeader.js';
import { RunUsageAndHistory } from './analysis-run/RunUsageAndHistory.js';
import { analysisRunPageCopy } from './analysis-run/run-page-copy.js';
import { useAnalysisRunMutation } from './analysis-run/useAnalysisRunMutation.js';
import { useBusinessProfileDraft } from './analysis-run/useBusinessProfileDraft.js';
import { useCompetitorForms } from './analysis-run/useCompetitorForms.js';
import { useReviewDrafts } from './analysis-run/useReviewDrafts.js';
import { useSourceForm } from './analysis-run/useSourceForm.js';

export function AnalysisRunPage(): ReactNode {
  const { id = '' } = useParams();
  const { language } = useLanguage();
  const { state, reload } = useAsync(useCallback(() => fetchAnalysisRun(id), [id]));
  const draft = useBusinessProfileDraft(state);
  const { busy, error, mutate } = useAnalysisRunMutation(reload);
  const sourceForm = useSourceForm();
  const competitorForms = useCompetitorForms(state);
  const drafts = useReviewDrafts(state);
  const copy = analysisRunPageCopy(language);
  const { profile } = draft;

  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed') {
    return <FailurePanel failure={state.failure} onRetry={reload} />;
  }
  const { run, attempts, artifacts, day2, day3, day4, day5, day6, day7, day8 } =
    state.value;
  const currentDraftId = day2.draft?.id ?? null;
  const mayAcquire = ['INTAKE', 'BUSINESS_EVIDENCE'].includes(run.currentStage);
  const mayReview =
    run.currentStage === 'BUSINESS_PROFILE_REVIEW' &&
    run.executionState === 'AWAITING_REVIEW' &&
    currentDraftId !== null;

  return (
    <div className="mj-analysis-run">
      <RunHeader run={run} day2={day2} error={error} copy={copy} />

      <AnalysisPipeline run={run} />

      <AnalysisStrategyBrief
        run={run}
        day2={day2}
        day3={day3}
        day4={day4}
        day5={day5}
        day6={day6}
        day7={day7}
        day8={day8}
      />

      <BusinessSourcesSection
        id={id}
        day2={day2}
        mayAcquire={mayAcquire}
        busy={busy}
        mutate={mutate}
        form={sourceForm}
        copy={copy}
      />

      <ReadinessAndEvidenceSection day2={day2} copy={copy} />

      {profile !== null ? (
        <BusinessProfileEditor
          id={id}
          profile={profile}
          draft={draft}
          currentDraftId={currentDraftId}
          mayReview={mayReview}
          busy={busy}
          mutate={mutate}
          copy={copy}
        />
      ) : null}

      <CompetitorSection
        id={id}
        run={run}
        attempts={attempts}
        day2={day2}
        day3={day3}
        busy={busy}
        mutate={mutate}
        forms={competitorForms}
        copy={copy}
      />

      <ContentCollectionSection
        id={id}
        run={run}
        day4={day4}
        busy={busy}
        mutate={mutate}
        drafts={drafts}
        copy={copy}
      />

      <ContentUnderstandingSection
        id={id}
        run={run}
        day5={day5}
        busy={busy}
        mutate={mutate}
        drafts={drafts}
      />

      <AudienceComparisonSection
        id={id}
        run={run}
        day6={day6}
        busy={busy}
        mutate={mutate}
        drafts={drafts}
      />

      <FindingsReviewSection
        id={id}
        run={run}
        day7={day7}
        busy={busy}
        mutate={mutate}
        drafts={drafts}
      />

      <OpportunitiesSection
        id={id}
        run={run}
        day8={day8}
        busy={busy}
        mutate={mutate}
        drafts={drafts}
      />

      <CreativeDirectionsSection
        id={id}
        run={run}
        day8={day8}
        busy={busy}
        mutate={mutate}
        drafts={drafts}
      />

      <RunUsageAndHistory
        day2={day2}
        attempts={attempts}
        artifacts={artifacts}
        copy={copy}
      />
    </div>
  );
}
